import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useMemo,
  useState,
} from "react";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { type SkillItem } from "../components/Chat/SlashMenu";
import {
  activeSlashToken,
  ghostSuffix,
  type SlashToken,
} from "../lib/slashToken";
import { useGroupMemberships } from "./useGroupMemberships";
import { useQuery } from "./useQuery";

export type { SkillItem };

type UseSlashSkillsParams = {
  spaceId: string | null | undefined;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (v: string) => void;
  onAfterAccept?: () => void;
  enabled?: boolean;
};

export function useSlashSkills({
  spaceId,
  textareaRef,
  value,
  setValue,
  onAfterAccept,
  enabled = true,
}: UseSlashSkillsParams) {
  const [slashToken, setSlashToken] = useState<SlashToken | null>(null);

  const [allSkills] = useQuery(enabled ? queries.skillsIndex() : undefined);
  const [spaceGrants] = useQuery(
    enabled && spaceId
      ? queries.roleGrantsByScope({ scopeId: spaceId })
      : undefined
  );
  const { userGroupIds: groupMemberships } = useGroupMemberships({ enabled });
  const availableSkills = useMemo<SkillItem[]>(() => {
    const enabledSkills = (allSkills ?? []).filter((s) => s.enabled);
    // Mirror what the AI can load: inside a space, only skills the space can use;
    // elsewhere, any skill the user can read (skillsIndex already enforces that).
    const usable =
      spaceId && spaceGrants
        ? enabledSkills.filter((s) =>
            isSupplierAvailableFromGrants(
              "space_uses_skills",
              spaceGrants,
              s.roleGrantsAsScope,
              groupMemberships
            )
          )
        : enabledSkills;
    return usable.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description,
    }));
  }, [allSkills, spaceId, spaceGrants, groupMemberships]);

  const [selectedSkills, setSelectedSkills] = useState<SkillItem[]>([]);
  const addSkill = useCallback((skill: SkillItem) => {
    setSelectedSkills((prev) =>
      prev.some((s) => s.id === skill.id) ? prev : [...prev, skill]
    );
  }, []);
  const removeSkill = useCallback((id: string) => {
    setSelectedSkills((prev) => prev.filter((s) => s.id !== id));
  }, []);
  const clearSkills = useCallback(() => setSelectedSkills([]), []);

  const syncSlashToken = useCallback((el: HTMLTextAreaElement) => {
    if (el.value === "") {
      setSlashToken(null);
      return;
    }
    setSlashToken(activeSlashToken(el.value, el.selectionStart ?? 0));
  }, []);

  const acceptSlashSkill = useCallback(
    (skill: SkillItem) => {
      if (!slashToken) return;
      const before = value.slice(0, slashToken.start);
      const after = value.slice(slashToken.end);
      // Add a trailing space only at the end of the input; mid-message there's already whitespace after the token.
      const inserted = `/${skill.name}${after === "" ? " " : ""}`;
      const caret = before.length + inserted.length;
      setValue(before + inserted + after);
      addSkill(skill);
      setSlashToken(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(caret, caret);
        }
        onAfterAccept?.();
      });
    },
    [value, slashToken, addSkill, textareaRef, setValue, onAfterAccept]
  );

  const slashMatch =
    slashToken && slashToken.query.length > 0
      ? (availableSkills.find((s) =>
          s.name.toLowerCase().startsWith(slashToken.query.toLowerCase())
        ) ?? null)
      : null;
  const slashCaret = slashToken
    ? slashToken.start + 1 + slashToken.query.length
    : 0;
  const slashGhost =
    slashToken && slashMatch && slashCaret === slashToken.end
      ? ghostSuffix(slashToken.query, slashMatch.name)
      : "";

  const handleSlashKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!(slashToken && slashMatch && slashGhost)) return false;
      if (e.key === "Tab") {
        e.preventDefault();
        acceptSlashSkill(slashMatch);
        return true;
      }
      if (
        e.key === "ArrowRight" &&
        e.currentTarget.selectionStart === e.currentTarget.selectionEnd &&
        e.currentTarget.selectionStart === slashToken.end
      ) {
        e.preventDefault();
        acceptSlashSkill(slashMatch);
        return true;
      }
      return false;
    },
    [slashToken, slashMatch, slashGhost, acceptSlashSkill]
  );

  return {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    slashToken,
    syncSlashToken,
    slashMatch,
    slashCaret,
    slashGhost,
    acceptSlashSkill,
    handleSlashKeyDown,
  };
}
